import type { FC } from 'react';
import React from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { AgentCard } from './AgentCard.tsx';
import type { AgentCardConfig, AgentCardLayout } from './agentCards.config.ts';

const rowStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.200'),
	width: '100%',
});

export const getCardLayout = (visibleCount: number): AgentCardLayout =>
	visibleCount <= 1 ? 'oneColumn' : 'twoColumn';

export type AgentCardRowProps = {
	cards: AgentCardConfig[];
	/** Called with a card's `key` when its dismiss control is clicked. */
	onDismiss?: (key: string) => void;
};

/**
 * Responsive row of "Rovo Agents for you" cards.
 *
 * - 2 cards: cards fill two equal columns and use the two-column illustration.
 * - 1 card: the remaining card fills the row and uses the one-column illustration.
 *
 * The row is presentational: it reflows for whatever cards it is given. The
 * visible set (and dismiss handling) is owned by the parent section.
 */
export const AgentCardRow: FC<AgentCardRowProps> = ({ cards, onDismiss }) => {
	if (cards.length === 0) {
		return null;
	}

	const layout = getCardLayout(cards.length);

	return (
		<div css={rowStyles}>
			{cards.map((card) => (
				<AgentCard
					key={card.key}
					card={card}
					layout={layout}
					onDismiss={onDismiss ? () => onDismiss(card.key) : undefined}
				/>
			))}
		</div>
	);
};
